import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { ShieldAlert, Save } from "lucide-react";

// Iter-202c — CEO-only block: configure which permissions are "critical" and
// trigger an alert email to the CEO whenever an ADMIN (≠ CEO) touches a custom
// role that contains them.

export default function CriticalPermsAlertBlock() {
  const [enabled, setEnabled] = useState(true);
  const [selected, setSelected] = useState([]);
  const [catalog, setCatalog] = useState({}); // { module: [{key,label,desc}] }
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get("/settings"),
          api.get("/permissions/catalog"),
        ]);
        setEnabled(s.data.critical_perms_email_alert_enabled !== false);
        setSelected(Array.isArray(s.data.critical_permissions) ? s.data.critical_permissions : []);
        setCatalog(c.data.catalog || {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Impossible de charger les paramètres");
      } finally { setLoaded(true); }
    })();
  }, []);

  const toggle = (perm) => {
    setSelected((prev) => prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]);
    setDirty(true);
  };

  const saveToggle = async (next) => {
    setSaving(true);
    try {
      await api.put("/settings", { critical_perms_email_alert_enabled: next });
      setEnabled(next);
      toast.success(next ? "Alertes activées" : "Alertes désactivées");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la sauvegarde");
    } finally { setSaving(false); }
  };

  const savePerms = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings", { critical_permissions: selected });
      setSelected(r.data.critical_permissions || []);
      setDirty(false);
      toast.success(`${(r.data.critical_permissions || []).length} permission(s) critiques enregistrées`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la sauvegarde");
    } finally { setSaving(false); }
  };

  const totalPerms = useMemo(() => Object.values(catalog).reduce((acc, mod) => acc + mod.length, 0), [catalog]);

  if (!loaded) return null;

  return (
    <div className="mb-8 border border-rose-200 bg-rose-50/30 rounded-lg p-5" data-testid="critical-perms-alert-block">
      <div className="flex items-start gap-3 mb-4">
        <ShieldAlert className="text-rose-600 mt-1" size={20} />
        <div className="flex-1">
          <h2 className="font-heading font-bold text-base">Alerte permissions critiques — email CEO</h2>
          <p className="text-xs text-zinc-600 mt-1 max-w-2xl">
            Reçoit un email immédiat quand un ADMIN (autre que vous) crée, modifie, supprime ou assigne
            un rôle qui touche à au moins une des permissions ci-dessous. Idéal pour surveiller les
            distributions de privilèges sensibles.
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={saveToggle}
          disabled={saving}
          data-testid="critical-perms-toggle"
        />
      </div>

      <div className={enabled ? "" : "opacity-50 pointer-events-none"}>
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-xs font-mono uppercase tracking-wide text-zinc-500">
            Permissions surveillées · <span className="text-rose-700 font-bold">{selected.length}</span> / {totalPerms}
          </div>
          {dirty && (
            <button
              onClick={savePerms}
              disabled={saving}
              data-testid="critical-perms-save"
              className="inline-flex items-center gap-1.5 text-xs px-3 h-8 bg-rose-600 text-white rounded-md hover:bg-rose-700 disabled:opacity-50"
            >
              <Save size={12} /> Enregistrer la sélection
            </button>
          )}
        </div>
        <div className="space-y-3">
          {Object.entries(catalog).map(([mod, perms]) => {
            const inMod = perms.filter((p) => selected.includes(p.key)).length;
            return (
              <div key={mod} className="bg-white border border-zinc-200 rounded p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-zinc-900">{mod}</div>
                  <span className="text-[10px] font-mono text-zinc-500">{inMod}/{perms.length}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {perms.map((p) => {
                    const checked = selected.includes(p.key);
                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => toggle(p.key)}
                        data-testid={`critical-perm-${p.key}`}
                        title={p.desc}
                        className={`text-[10px] font-mono px-2 py-1 rounded border transition-colors ${
                          checked
                            ? "bg-rose-100 border-rose-300 text-rose-800"
                            : "bg-zinc-50 border-zinc-200 text-zinc-600 hover:border-rose-200 hover:bg-rose-50"
                        }`}
                      >
                        {checked ? "✓ " : ""}{p.key}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
